import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  History,
  Users,
  Send,
  Facebook,
  Workflow,
  Wallet,
  Settings,
  Puzzle,
  Tag,
  FolderKanban,
  UserCircle,
  LogOut,
  Menu,
  X,
  Database,
  FileText,
  Megaphone,
  MessageCircle,
  Contact,
  Download,
  ChevronUp,
  KeyRound,
  ChevronDown,
  UserPlus,
  Receipt,
  BarChart3,
  ListChecks,
  BookOpen,
} from "lucide-react";
import logo from "../../assets/i3.png";
import { logout, isSuperAdmin } from "../../utils/auth";
import { getCurrentUser } from "../../apis/auth";
import { hasSectionAccess } from "../../utils/sectionAccess";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const navRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const isActive = (paths = []) => {
    return paths.some((p) => location.pathname.startsWith(p));
  };

  const handleLogout = () => {
    logout();
  };

  const checkScrollability = useCallback(() => {
    if (navRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = navRef.current;
      const hasScrollableContent = scrollHeight > clientHeight;
      setCanScrollUp(hasScrollableContent && scrollTop > 5);
      setCanScrollDown(
        hasScrollableContent && scrollTop < scrollHeight - clientHeight - 5
      );
    }
  }, []);

  const scrollUp = () => {
    if (navRef.current) {
      navRef.current.scrollBy({ top: -80, behavior: "smooth" });
      setTimeout(checkScrollability, 300);
    }
  };

  const scrollDown = () => {
    if (navRef.current) {
      navRef.current.scrollBy({ top: 80, behavior: "smooth" });
      setTimeout(checkScrollability, 300);
    }
  };

  // Sections gate the menu. Initialize from localStorage for instant render,
  // then refresh from the server so permission changes (e.g. a revoked
  // Contacts section) take effect without forcing the agent to re-login.
  const [userSections, setUserSections] = useState(() => {
    try {
      const s = localStorage.getItem("sections");
      return s ? JSON.parse(s) : [];
    } catch {
      return [];
    }
  });
  const [agentPermissions, setAgentPermissions] = useState(() => {
    try {
      const p = localStorage.getItem("agentPermissions");
      return p ? JSON.parse(p) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    let cancelled = false;
    getCurrentUser()
      .then((res) => {
        const fresh = res?.data?.sections;
        const perms = res?.data?.permissions;
        if (!cancelled) {
          if (Array.isArray(fresh)) {
            localStorage.setItem("sections", JSON.stringify(fresh));
            setUserSections(fresh);
          }
          if (Array.isArray(perms)) {
            localStorage.setItem("agentPermissions", JSON.stringify(perms));
            setAgentPermissions(perms);
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  
  // All menu items with their required sections
  // section: string | string[] - if array, user needs ANY of these sections
  const allMenuItems = [
    {
      path: "/dashboard",
      icon: LayoutDashboard,
      label: "Dashboard",
      section: "",
    },
    {
      path: "/waba",
      icon: Users,
      label: "Phone Numbers",
      section: "phone_numbers",
    },
    {
      path: "/templates",
      icon: FileText,
      label: "Templates",
      section: "templates",
    },
    {
      path: "/broadcast",
      icon: Megaphone,
      label: "Broadcasting",
      section: ["broadcasting", "campaigns"],
    },
    {
      path: "/contacts",
      icon: Contact,
      label: "Contacts",
      section: "contacts_management",
    },
    {
      path: "/flow-list",
      icon: Workflow,
      label: "Flows",
      section: "flows",
    },
    {
      path: "/whatsapp-flows",
      icon: ListChecks,
      label: "WA Flows",
      section: "flows",
    },
    {
      path: "/live-chats",
      icon: MessageCircle,
      label: "Live Chats",
      section: ["livechats", "livechats.own", "livechats.all"],
    },
    {
      path: "/agents",
      icon: UserPlus,
      label: "Agents",
      section: "agents",
    },
    {
      path: "/chat-statuses",
      icon: Tag,
      label: "Chat Statuses",
      section: "agents",
    },
    {
      path: "/agent-analysis",
      icon: BarChart3,
      label: "Agent Analysis",
      section: "agents",
    },
    {
      path: "/download-center",
      icon: Download,
      label: "Downloads",
      section: "downloads",
    },
    {
      path: "/api-keys",
      icon: KeyRound,
      label: "API Keys",
      superAdminOnly: true,
    },
    {
      path: "/api-docs",
      icon: BookOpen,
      label: "API Docs",
      section: "",
    },
  ];

  // Helper: check if user has access to a section (or any of multiple sections).
  // Phone-scoped sections (contacts, templates, …) require an actual phone grant.
  const canAccessSection = (sectionOrSections) =>
    hasSectionAccess(sectionOrSections, userSections, agentPermissions);

  // Filter menu items based on user's sections + phone grants
  const menuItems = allMenuItems.filter((item) => {
    if (item.superAdminOnly && !isSuperAdmin()) return false;
    if (!item.section) return true;
    return canAccessSection(item.section);
  });

  const bottomMenuItems = [
    {
      path: "/profile",
      icon: UserCircle,
      label: "Profile",
      section: "profile",
    },
    {
      path: "/invoices",
      icon: Receipt,
      label: "Invoices",
      section: "",
    },
    {
      path: "/settings",
      icon: Settings,
      label: "Settings",
      section: "settings",
    },
    {
      path: "#",
      icon: LogOut,
      label: "Logout",
      onClick: handleLogout,
      isLogout: true,
    },
  ].filter((item) => !item.section || canAccessSection(item.section));

  useEffect(() => {
    const navElement = navRef.current;
    if (navElement) {
      const checkMultiple = () => {
        checkScrollability();
        setTimeout(checkScrollability, 50);
        setTimeout(checkScrollability, 200);
        setTimeout(checkScrollability, 500);
      };

      checkMultiple();

      navElement.addEventListener("scroll", checkScrollability, {
        passive: true,
      });

      const resizeObserver = new ResizeObserver(() => {
        setTimeout(checkScrollability, 50);
      });
      resizeObserver.observe(navElement);

      const handleResize = () => {
        setTimeout(checkScrollability, 100);
      };
      window.addEventListener("resize", handleResize);

      return () => {
        navElement.removeEventListener("scroll", checkScrollability);
        resizeObserver.disconnect();
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isOpen, checkScrollability, menuItems.length]);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar Container with Padding */}
      <div
        className={`
          fixed left-0 top-0 z-50 h-screen p-3
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        {/* Sidebar */}
        <aside
          className="w-20 h-full rounded-[24px] flex flex-col overflow-hidden"
          style={{ backgroundColor: "#17181b" }}
        >
          {/* Logo */}
          <div className="py-4 px-2 flex items-center justify-center border-b border-white/10 relative flex-shrink-0">
            <img
              src={logo}
              alt="BluTec PING"
              className="h-10 w-10 rounded-lg"
            />
            <button
              onClick={toggleSidebar}
              className="absolute right-1.5 top-4 text-white lg:hidden"
              aria-label="Close sidebar"
            >
              <X size={18} />
            </button>
          </div>

          {/* Main Navigation - Scrollable */}
          <div className="flex-1 relative min-h-0 overflow-hidden">
            {/* Scroll Up Indicator */}
            {canScrollUp && (
              <button
                onClick={scrollUp}
                className="absolute top-0 left-0 right-0 z-20 flex items-center justify-center h-10 bg-gradient-to-b from-[#17181b] via-[#17181b]/98 to-transparent hover:from-[#2a2b30] transition-all duration-200 cursor-pointer pointer-events-auto"
                aria-label="Scroll up"
              >
                <div className="bg-white/10 rounded-full p-1">
                  <ChevronUp
                    size={18}
                    className="text-white/60"
                    strokeWidth={2.5}
                  />
                </div>
              </button>
            )}

            <nav
              ref={navRef}
              className="h-full overflow-y-auto overflow-x-hidden py-3 px-2 scrollbar-hide"
            >
              <ul className="space-y-1">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.paths || [item.path]);

                  return (
                    <li key={item.path} className="relative">
                      <Link
                        to={item.path}
                        className={`
                          flex flex-col items-center justify-center py-3 px-2 rounded-xl
                          transition-all duration-200
                          ${
                            active
                              ? "text-black"
                              : "text-white/50 hover:text-white/80 hover:bg-white/5"
                          }
                        `}
                        style={{
                          backgroundColor: active ? "#eefa94" : "transparent",
                        }}
                        onClick={() =>
                          window.innerWidth < 1024 && toggleSidebar()
                        }
                      >
                        {/* Icon */}
                        <Icon
                          size={20}
                          strokeWidth={active ? 2 : 1.5}
                          className="mb-1"
                        />

                        {/* Label */}
                        <span className="text-[9px] font-medium text-center leading-tight">
                          {item.label}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Scroll Down Indicator */}
            {canScrollDown && (
              <button
                onClick={scrollDown}
                className="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-center h-10 bg-gradient-to-t from-[#17181b] via-[#17181b]/98 to-transparent hover:from-[#2a2b30] transition-all duration-200 cursor-pointer pointer-events-auto"
                aria-label="Scroll down"
              >
                <div className="bg-white/10 rounded-full p-1">
                  <ChevronDown
                    size={18}
                    className="text-white/60"
                    strokeWidth={2.5}
                  />
                </div>
              </button>
            )}
          </div>

          {/* Bottom Menu Items (Profile, Settings, Logout) - Fixed */}
          <div className="border-t border-white/10 pt-2 pb-3 px-2 flex-shrink-0">
            <ul className="space-y-1">
              {bottomMenuItems.map((item) => {
                const Icon = item.icon;
                const active = !item.isLogout && isActive([item.path]);

                if (item.isLogout) {
                  return (
                    <li key="logout" className="relative">
                      <button
                        onClick={() => {
                          if (window.innerWidth < 1024) {
                            toggleSidebar();
                          }
                          handleLogout();
                        }}
                        className="w-full flex flex-col items-center justify-center py-3 px-2 rounded-xl transition-all duration-200 text-white/50 hover:text-white/80 hover:bg-white/5"
                      >
                        {/* Icon */}
                        <Icon size={20} strokeWidth={1.5} className="mb-1" />

                        {/* Label */}
                        <span className="text-[9px] font-medium text-center leading-tight">
                          {item.label}
                        </span>
                      </button>
                    </li>
                  );
                }

                return (
                  <li key={item.path} className="relative">
                    <Link
                      to={item.path}
                      className={`
                        flex flex-col items-center justify-center py-3 px-2 rounded-xl
                        transition-all duration-200
                        ${
                          active
                            ? "text-black"
                            : "text-white/50 hover:text-white/80 hover:bg-white/5"
                        }
                      `}
                      style={{
                        backgroundColor: active ? "#eefa94" : "transparent",
                      }}
                      onClick={() => {
                        if (window.innerWidth < 1024) {
                          toggleSidebar();
                        }
                        if (item.onClick) {
                          item.onClick();
                        }
                      }}
                    >
                      {/* Icon */}
                      <Icon
                        size={20}
                        strokeWidth={active ? 2 : 1.5}
                        className="mb-1"
                      />

                      {/* Label */}
                      <span className="text-[9px] font-medium text-center leading-tight">
                        {item.label}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>
      </div>

      {/* Mobile toggle button */}
      <button
        onClick={toggleSidebar}
        className="fixed top-6 left-6 z-40 lg:hidden text-white p-2 rounded-xl shadow-lg"
        style={{ backgroundColor: "#17181b" }}
        aria-label="Toggle sidebar"
      >
        <Menu size={20} />
      </button>
    </>
  );
};

export default Sidebar;
